import { Ionicons } from '@expo/vector-icons';
import React, { useCallback, useState } from 'react';
import { ActivityIndicator, Alert, Platform, Pressable, Text, View } from 'react-native';
import { logger } from '@/lib/logger';
import {
  OfflineApiError,
  deleteDownload,
  formatBytes,
  pauseDownload,
  resumeDownload,
  retryDownload,
  startDownload,
  useDownload,
  useIsOnline,
} from '@/lib/offline';
import type { LessonPageData } from '@/supabase/services/lessonOptimized';
import type { LessonVideo } from '@/types';

/**
 * Per-video "save offline" control for the lesson page (native only).
 * Idle -> preparing -> downloading (percent) -> saved. Tapping an active or
 * finished download offers pause/resume/remove via a native alert.
 */
interface Props {
  pageData: LessonPageData | null;
  video: LessonVideo | null;
  userId: string | null | undefined;
}

const SIZE = 40;

function describeError(err: unknown): string {
  if (err instanceof OfflineApiError) {
    if (err.status === 403) return "Your membership doesn't include this lesson.";
    if (err.status === 422) return "This video can't be downloaded yet. Please try another lesson.";
    if (err.status === 401) return 'Please sign in again to download lessons.';
  }
  return 'Could not start the download. Check your connection and try again.';
}

export default function DownloadButton({ pageData, video, userId }: Props) {
  const record = useDownload(video?.id);
  const online = useIsOnline();
  const [busy, setBusy] = useState(false);

  const begin = useCallback(async () => {
    if (!pageData || !video || !userId) return;
    if (!online) {
      Alert.alert('You are offline', 'Connect to the internet to download this lesson.');
      return;
    }
    setBusy(true);
    try {
      await startDownload({ pageData, video, userId });
    } catch (err) {
      logger.warn('[offline] start download failed', { videoId: video.id, err });
      Alert.alert('Download failed', describeError(err));
    } finally {
      setBusy(false);
    }
  }, [pageData, video, userId, online]);

  const confirmRemove = useCallback(() => {
    if (!video) return;
    Alert.alert('Remove download?', 'The video will be deleted from this device.', [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Remove', style: 'destructive', onPress: () => void deleteDownload(video.id) },
    ]);
  }, [video]);

  const onPress = useCallback(() => {
    if (!video) return;
    const status = record?.status;
    if (!record || status === 'failed') {
      if (record && status === 'failed' && !pageData) {
        void retryDownload(video.id);
        return;
      }
      void begin();
      return;
    }
    if (status === 'complete') {
      Alert.alert('Saved for offline', `${formatBytes(record.bytesDownloaded)} on this device.`, [
        { text: 'Keep', style: 'cancel' },
        { text: 'Remove download', style: 'destructive', onPress: confirmRemove },
      ]);
      return;
    }
    if (status === 'downloading' || status === 'queued') {
      Alert.alert('Downloading…', undefined, [
        { text: 'Continue', style: 'cancel' },
        { text: 'Pause', onPress: () => void pauseDownload(video.id) },
        { text: 'Cancel download', style: 'destructive', onPress: () => void deleteDownload(video.id, 'system') },
      ]);
      return;
    }
    if (status === 'paused') {
      Alert.alert('Download paused', undefined, [
        { text: 'Resume', onPress: () => void resumeDownload(video.id) },
        { text: 'Cancel download', style: 'destructive', onPress: () => void deleteDownload(video.id, 'system') },
        { text: 'Close', style: 'cancel' },
      ]);
    }
  }, [record, video, pageData, begin, confirmRemove]);

  if (Platform.OS === 'web' || !video) return null;

  const status = record?.status;
  const percent =
    record && record.bytesTotal && record.bytesTotal > 0
      ? Math.min(99, Math.floor((record.bytesDownloaded / record.bytesTotal) * 100))
      : null;

  let icon: keyof typeof Ionicons.glyphMap = 'download-outline';
  let color = '#0D0D0D';
  let label = 'Download for offline';
  let body: React.ReactNode = null;
  if (busy || status === 'queued') {
    label = 'Preparing download';
    body = <ActivityIndicator size="small" color="#0D0D0D" />;
  } else if (status === 'downloading') {
    label = `Downloading ${percent ?? 0}%`;
    body = <Text style={{ fontSize: 11, fontWeight: '700', color: '#0D0D0D' }}>{percent ?? 0}%</Text>;
  } else if (status === 'paused') {
    icon = 'pause';
    label = 'Download paused';
  } else if (status === 'complete') {
    icon = 'checkmark-circle';
    color = '#2E7D32';
    label = 'Saved for offline';
  } else if (status === 'failed') {
    icon = 'refresh';
    color = '#B3261E';
    label = 'Download failed, tap to retry';
  }

  return (
    <Pressable
      onPress={onPress}
      disabled={busy}
      accessibilityRole="button"
      accessibilityLabel={label}
      hitSlop={6}
      style={({ pressed }) => ({
        width: SIZE,
        height: SIZE,
        borderRadius: SIZE / 2,
        borderWidth: 1,
        borderColor: pressed ? '#0D0D0D' : '#C9C5C3',
        backgroundColor: '#FFFFFF',
        alignItems: 'center',
        justifyContent: 'center',
        opacity: busy ? 0.7 : 1,
      })}
    >
      <View style={{ alignItems: 'center', justifyContent: 'center' }}>
        {body ?? <Ionicons name={icon} size={20} color={color} />}
      </View>
    </Pressable>
  );
}
